import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { APIService } from '../../shared/api.service';
import { Asset } from 'app/classes/asset';
import { Org } from 'app/classes/org';
import { User } from 'app/classes/user';
import { Site } from 'app/classes/site';
import { Gateway } from 'app/classes/gateway';
import { DerivedAsset } from 'app/classes/derived-asset';
import { lastValueFrom } from 'rxjs';
import { CombinedCollectionItem } from 'app/shared/site.service';
import { IDataTableSortColumn } from 'app/layout/data-table/data-table/data-table.component';
import { SitePlanComplete } from 'app/shared/floorplan.service';
import { AssetService } from 'app/shared/asset.service';
import { Building } from 'app/classes/building';

@Injectable()
export class AdminInfraService {
	static readonly API_URL = ' https://20r3t5z7hf.execute-api.eu-west-2.amazonaws.com/prod/infra';

	constructor(private apiService: APIService, private http: HttpClient, private assetService: AssetService) { }

	async getDerivedAssets(): Promise<DerivedAsset[]> {
		const params = new HttpParams().set('action', 'get-derived-assets');
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return response.assets.map(a => new DerivedAsset(a));
	}

	async updateAssets(assets: Asset[]): Promise<any> {
		const params = new HttpParams().set('action', 'update-assets');
		const options = { headers: this.apiService.getUAOHeaders().headers, params };

		return await lastValueFrom(this.http.post<any>(AdminInfraService.API_URL, { assets }, options));
	}

	async getMeters(orgId: number): Promise<{ meters: IMeterItem[] }> {
		const params = new HttpParams().set('action', 'get-meters').set('orgid', orgId);
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		response.meters = response.meters.map(m => {
			m.createdAt = new Date(m.createdAt);
			m.firstvalueAt = new Date(m.firstvalueAt);
			return m;
		});

		return response;
	}

	async getEgress(orgId: number): Promise<IGetEgress> {
		const params = new HttpParams().set('action', 'get-egress').set('orgid', orgId);
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		response.initialstate.forEach(row => row.isActive = row.tpDisabled !== 1);

		return response;
	}

	async getTrackingIssues(orgId: number): Promise<{ issues: any[] }> {
		const params = new HttpParams().set('action', 'get-trcking-issues').set('orgid', orgId);
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return response;
	}

	async getFloorplan(floorplanId: number): Promise<SitePlanComplete> {
		const params = new HttpParams().set('action', 'get-floorplan').set('id', floorplanId);
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));
		const sitePlanComplete = new SitePlanComplete(response, this.assetService);

		return sitePlanComplete;
	}

	async getFloorplans(orgId: number): Promise<{ floorplans: any[] }> {
		const params = new HttpParams().set('action', 'get-floorplans').set('orgid', orgId);
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return response;
	}

	async createWeatherLocation(lat: number, lng: number, title: string): Promise<any> {
		const params = new HttpParams().set('action', 'create-weather-location');
		const options = { headers: this.apiService.getUAOHeaders().headers, params };

		return await lastValueFrom(this.http.post<any>(AdminInfraService.API_URL, { lat, lng, title }, options));
	}

	async updateFloorplan(id: number, changes: { updates?: any, inserts?: any, deletes?: any }): Promise<any> {
		const params = new HttpParams().set('action', 'update-floorplan').set('id', id.toString());
		const options = { headers: this.apiService.getUAOHeaders().headers, params };

		return await lastValueFrom(this.http.post<any>(AdminInfraService.API_URL, changes, options));
	}

	async getSharing(orgId: number): Promise<{ sharing: any[], orgs: any[], consumedSites: any[], sitesDashboards: any[] }> {
		const params = new HttpParams().set('action', 'get-sharing').set('orgid', orgId);
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return response;
	}

	async getRules(orgId: number): Promise<{ rules: any[] }> {
		const params = new HttpParams().set('action', 'get-rules').set('orgid', orgId);
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return response;
	}

	async getCollections(orgId: number): Promise<IGetCollections> {
		const params = new HttpParams().set('action', 'get-collections').set('orgid', orgId);
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		response.collections.forEach(collection => {
			collection.typeTitle = response.config.collectionTypes.find(type => type.id === collection.typeId).title;
		});

		return response;
	}

	async fixIngestRatesForOrg(orgId: number, category): Promise<{ message: string }> {
		let params = new HttpParams();
		params = params.set('action', 'fix-orgs-ingest-rates');
		params = params.set('orgid', String(orgId));
		if (category) {
			params = params.set('category', category);
		}
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return response;
	}

	async fixMeterReadings(orgId: number): Promise<{ message: string }> {
		let params = new HttpParams();
		params = params.set('action', 'fix-meter-readings');
		params = params.set('orgid', String(orgId));

		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return response;
	}

	async fixMissingMeters(orgId: number): Promise<{ message: string }> {
		let params = new HttpParams();
		params = params.set('action', 'fix-missing-meters');
		params = params.set('orgid', String(orgId));

		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return response;
	}

	async getOrgs(): Promise<IGetOrgs> {
		const params = new HttpParams().set('action', 'get-orgs');
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<IGetOrgs>(AdminInfraService.API_URL, options));

		response.orgs = response.orgs.map(org => new Org(org));

		return response;
	}

	async getAssetsNotReported(days: number): Promise<{ assets: any[], orgs: any[] }> {
		const params = new HttpParams().set('action', 'get-assets-not-reported').set('days', String(days));
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return response;
	}

	async getClosestWeatherStationForSite(siteId: number): Promise<{ dist: any, keys: any[], metobs: { title: string, lat: number, long: number, country_id: string, type: string, createdAt: Date }[] }> {
		const params = new HttpParams().set('action', 'get-closest-weather-station').set('id', String(siteId));
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return response;
	}

	async getSimpleAssetsForOrg(orgId: number, sort: IDataTableSortColumn = null, limit: number = 10000, gatewayId = null): Promise<{ assets: IGetSimpleAsset[], assetTypes: any[] }> {
		const ob = sort ? sort.attribute + '-' + sort.sortDirection : '';
		const params = new HttpParams().set('action', 'get-assets').set('orgid', String(orgId)).set('limit', limit).set('oby', ob);
		if (gatewayId) {
			params.set('gid', String(gatewayId));
		}
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		response.assets = response.assets.map(asset => {
			return {
				id: asset.id,
				title: asset.title,
				orgShortTitle: asset.orgShortTitle,
				assetType_id: asset.assetType_id,
				assetTypeTitle: asset.assetTypeTitle,
				value: asset.value,
				ingestRate: asset.ingestRate,
				updatedAt: new Date(asset.updatedAt),
				createdAt: new Date(asset.createdAt),
				gateway_id: asset.gateway_id,
				identifier: asset.identifier
			};
		});

		return response;
	}

	async getAssetsForOrg(orgId: number): Promise<IGetAssets> {
		const params = new HttpParams().set('action', 'get-assets').set('orgid', String(orgId));
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<IGetAssets>(AdminInfraService.API_URL, options));

		response.assets = response.assets.map(asset => new Asset(asset));

		return response;
	}

	async getFeaturesForOrg(orgId: number): Promise<IGetOrgFeature[]> {
		const params = new HttpParams().set('action', 'get-features').set('orgid', String(orgId));
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return response.features;
	}

	async featureChangeForOrg(orgId: number, feature: IGetOrgFeature): Promise<any> {
		const params = new HttpParams().set('action', 'update-feature').set('orgid', String(orgId));
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.post<any>(AdminInfraService.API_URL, feature, options));

		return response.features;
	}

	async getGatewaysForOrg(orgId: number): Promise<IGetGateways> {
		const params = new HttpParams().set('action', 'get-gateways').set('orgid', String(orgId));
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<IGetGateways>(AdminInfraService.API_URL, options));

		response.gateways = response.gateways.map(gateway => new Gateway(gateway));

		return response;
	}

	async getGatewaysForSite(siteId: number): Promise<IGetGateways> {
		const params = new HttpParams().set('action', 'get-gateways').set('siteid', String(siteId));
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<IGetGateways>(AdminInfraService.API_URL, options));

		response.gateways = response.gateways.map(gateway => new Gateway(gateway));

		return response;
	}

	async getAllSites(): Promise<IGetSites> {
		const params = new HttpParams().set('action', 'get-sites');
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<IGetSites>(AdminInfraService.API_URL, options));

		response.sites = response.sites.map(site => new Site(site));

		return response;
	}

	async getAllBuildings(): Promise<IGetBuildings> {
		const params = new HttpParams().set('action', 'get-all-buildings');
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<IGetBuildings>(AdminInfraService.API_URL, options));

		response.buildings = response.buildings.map(building => new Building(building));

		return response;
	}


	async getAllSitesExtended(): Promise<IGetSites> {
		const params = new HttpParams().set('action', 'get-sites').set('extended', '1');
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<IGetSites>(AdminInfraService.API_URL, options));

		response.sites = response.sites.map(site => new Site(site));

		return response;
	}

	async getSitesForOrg(orgId: number): Promise<IGetSites> {
		const params = new HttpParams().set('action', 'get-sites').set('orgid', String(orgId));
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<IGetSites>(AdminInfraService.API_URL, options));

		response.sites = response.sites.map(site => new Site(site));

		return response;
	}

	async getSite(siteId: number): Promise<{ site: Site, settings: any[] }> {
		const params = new HttpParams().set('action', 'get-site').set('id', String(siteId));
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		response.site = new Site(response.site);

		return response;
	}

	async updateSiteChanges(site: Site, changes: any, moveSetpoints = false): Promise<{ site: Site }> {
		const payload = { changes, moveSetpoints, id: site.id, addressId: site.addressId };
		const params = new HttpParams().set('action', 'update-site-changes').set('id', String(site.id));
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.post<any>(AdminInfraService.API_URL, payload, options));

		return response;
	}

	async updateSite(site: Site, moveSetpoints = true): Promise<{ site: Site }> {
		const params = new HttpParams().set('action', 'update-site');
		const payload = { site: site.serialise(), moveSetpoints: moveSetpoints };
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.post<any>(AdminInfraService.API_URL, payload, options));

		return response;
	}

	async deleteSite(siteId: number, step: number = 1): Promise<any> {
		const params = new HttpParams().set('action', 'delete-site').set('id', String(siteId)).set('step', String(step));
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return response;
	}

	async getSiteSetpoints(siteId: number): Promise<{ setpoints: any[] }> {
		const params = new HttpParams().set('action', 'get-site-setpoints').set('id', String(siteId));
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return response;
	}

	async fixSiteSetpoints(siteId: number): Promise<any> {
		const params = new HttpParams().set('action', 'fix-site-setpoints').set('id', String(siteId));
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return response;
	}

	async getOrg(orgId: number): Promise<IGetOrg> {
		const params = new HttpParams().set('id', String(orgId)).set('action', 'get-org');
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));
		const org = new Org(response.org);
		const users = response.users.map(user => new User(user));
		org.users = users;

		return { org, users };
	}

	async getUsersForOrg(orgId: number, isActive = true): Promise<{ users: User[] }> {
		const params = new HttpParams().set('action', 'get-users').set('is-active', isActive ? 1 : 0).set('orgid', String(orgId));
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const users = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return users;
	}

	async getAllUsers(isActive = true): Promise<{ users: User[] }> {
		const params = new HttpParams().set('action', 'get-all-users').set('is-active', isActive ? 1 : 0)
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const users = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return users;
	}

	async getAssetsForType(assetTypeId: number): Promise<{ assets: Asset[], stats: { count: number } }> {
		return new Promise((resolve) => {
			const url = `${AdminInfraService.API_URL}/?a=get-assets-for-type&id=${assetTypeId}`;

			return this.http
				.get<any>(url, this.apiService.getUAOHeaders())
				.subscribe(b => {
					b.assets = b.assets.map(a => new Asset(a));
					resolve(b);
				});
		});
	}

	async getTypes(): Promise<IAdminInfraGetTypes> {
		return new Promise((resolve) => {
			const url = `${AdminInfraService.API_URL}/?a=get-types`;

			return this.http
				.get<any>(url, this.apiService.getUAOHeaders())
				.subscribe(b => {
					resolve(b);
				});
		});
	}

	async getWeatherStations(): Promise<any> {
		return new Promise((resolve) => {
			const url = `${AdminInfraService.API_URL}/?a=get-weather-stations`;

			return this.http
				.get<any>(url, this.apiService.getUAOHeaders())
				.subscribe(b => {
					resolve(b);
				});
		});
	}

	async getAssetMappings(): Promise<IIngestAssetMap[]> {
		const params = new HttpParams().set('action', 'get-mappings');
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return response;
	}

	async getEmails(searchText: string = ''): Promise<{ emails: any[], users: any[] }> {
		const params = new HttpParams().set('action', 'get-emails').set('searchtext', searchText);
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return response;
	}

	async getEmailItem(emailId: number): Promise<{ email: any }> {
		const params = new HttpParams().set('action', 'get-email-item').set('id', String(emailId));
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return response;
	}

	async getSimpleEmails(): Promise<{ emails: ISimpleEmailItem[] }> {
		const params = new HttpParams().set('action', 'get-emails-simple');
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return response;
	}

	async getEmailsForAddress(address: string): Promise<{ emails: any[], counts: { emailCount: number } }> {
		const params = new HttpParams().set('action', 'get-user-emails').set('email', address);
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return response;
	}

	async fixAddress(addressId: number): Promise<any> {
		const params = new HttpParams().set('action', 'fix-address').set('id', String(addressId));
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return response;
	}

	async getMLList(orgId: number): Promise<IGetMLList> {
		const params = new HttpParams().set('action', 'get-ml-list').set('orgid', String(orgId));
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return response;
	}

	async runMLGetData(mlId: number): Promise<any> {
		const params = new HttpParams().set('action', 'run-ml-get-data').set('id', String(mlId));
		const options = { headers: this.apiService.getUAOHeaders().headers, params };
		const response = await lastValueFrom(this.http.get<any>(AdminInfraService.API_URL, options));

		return response;
	}
}

export interface IGetMLList {
	list: { id: number, assetCount: number, description: string, title: string, mltTitle: string, org_id: number, createdAt: Date, ml_thirdparty_id: number, collection_id: number }[],
	collections: { id: number, title: string, site_id: number, createdAt: Date, type_id: number, assets: Asset[] }[],
	assets: Asset[]

}

export interface ISimpleEmailItem {
	address: string;
	id: number;
	isComplaint: number;
	isSuppressed: number;
	messageId: string | null;
	updatedAt: string;
	usedCount: number;
	isUser: boolean;
}

export interface IIngestAssetMap {
	id: number;
	integrationType: 'lorawan';
	identifierIn: string;
	identifierOut: string;
	assetTypeId: number;
	title: string;
	assetTitle?: string | null;
	isSubAsset: number;
	dataType?: 'integer' | 'float1' | 'float2' | null;
	notes?: string | null;
	matchTo?: string;
}

export interface IGetEgressAPIAsset {
	asset_id: number;
	endpoint: string;
	api_id: number;
	property: string;
	action: string;
	isActive: number;
	value: null | string | number; // Adjust types as needed based on possible values
	pollValue: string;
	createdAt: string;
	lastValue: string;
	initialstate: number;
	pushedAt: string;
	pushedStatusCode: number;
	zeroIfMissed: number;
	org_id: number;
}

export interface IGetEgress {
	initialstate: { gateway_id: string, embedLink: string, createdAt: Date }[];
	api: IGetEgressAPIAsset[];
	frameworkAccess: IGetEgressAPIaws[];
	documents: { key: string, table: string, table_id: string, isDeleted: 'N' | 'Y', ext: string }[];
}

export interface IGetEgressAPIaws {
	id: number;
	apikey: string;
	title: string;
	orgId: number;
	createdAt: Date | string;
	createdBy: number | null;
	isEnabled: boolean;
	usageplanId: string | null;
	awsapikeyId: string | null;
	awsapiId: string | null;
	ratePer: 'day' | 'month' | 'year' | null;
	rateValue: number | null;
}

export interface IGetCollections {
	collections: CombinedCollectionItem[];
}

export interface IAdminInfraGetTypes {
	assetTypes: IAdminInfraAssetType[];
}

export interface IAdminInfraAssetType {
	id: number;
	title: string;
	category: string;
	isActive: boolean;
	dataType: string;
	min: number;
	max: number;
}

export interface IGetOrgs {
	orgs: Org[];
}

export interface IGetAssets {
	assets: Asset[];
}

export interface IGetSimpleAsset {
	id: number;
	title: string;
	assetType_id: number;
	assetTypeTitle: string;
	value: string;
	ingestRate: number;
	updatedAt: Date;
	createdAt: Date;
	gateway_id: string;
	identifier: string;
	orgShortTitle?: string;
	siteTitle?: string;
}

export interface IGetGateways {
	gateways: Gateway[];
}

export interface IGetSites {
	sites: Site[];
}

export interface IGetBuildings {
	buildings: Building[];
}

export interface IGetOrg {
	org: Org;
	users: User[];
}

export interface IMeterItem {
	id: number;
	asset_id: number;
	title: string;
	firstvalue: number;
	firstvalueAt: string;
	createdAt: Date;
}

export interface IGetOrgFeature {
	id: number;
	notes: string;
	labels: string;
	createdAt: Date;
}
